import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Cloud, HardDrive, Loader2, MonitorSmartphone, Printer, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { BRAND } from "../lib/brand";
import { fmtBytes } from "../lib/desktop";
import { useAuth } from "../context/AuthContext";
import { LicensePanel } from "../components/LicensePanel";
import { Button } from "../components/ui/button";
import { ThemeToggle } from "../components/ThemeToggle";

const POINTS = [
  [HardDrive, "100% lokal & offline", "Database tersimpan di komputer kasir. Tetap berjalan tanpa internet maupun domain."],
  [Cloud, "Sinkron otomatis ke cloud", "Saat online, transaksi & stok terkirim otomatis — owner pantau live dari HP."],
  [Printer, "Printer, laci & scanner", "Dukungan USB/Serial/Bluetooth seperti versi web, plus cetak struk langsung."],
  [ShieldCheck, "Aman", "Password terenkripsi, akses per role, backup file kapan saja."],
];
const STEPS = [
  "Masuk sebagai owner, lakukan pembayaran & unggah bukti transfer.",
  "Setelah disetujui admin, klik Unduh lalu jalankan file installer (.exe).",
  "Jika muncul \"Windows protected your PC\", klik More info → Run anyway.",
  "Buka MUFA POS dari ikon desktop, pilih Buat toko baru atau Ambil data toko dari akun cloud.",
  "Agar dipantau dari HP: Pengaturan → Cloud & Desktop → Hubungkan.",
];

const Access = ({ rel }) => {
  const { user, isOwner } = useAuth();
  const [lic, setLic] = useState(null);
  const load = useCallback(() => api.get("/license/me").then(({ data }) => setLic(data)).catch(() => setLic(false)), []);
  useEffect(() => { if (isOwner) load(); }, [isOwner, load]);
  if (!rel || user === null) return <Loader2 className="h-5 w-5 animate-spin" />;
  if (!rel.available) return <div className="text-sm rounded-lg bg-muted px-4 py-3" data-testid="download-unavailable">Installer sedang disiapkan. Silakan cek kembali nanti.</div>;
  const meta = <span className="text-sm text-muted-foreground block" data-testid="download-desktop-meta">Versi {rel.version} · {fmtBytes(rel.size)}</span>;
  if (!user) return (
    <div className="space-y-3" data-testid="download-login-required">
      <p className="text-sm text-muted-foreground">Unduhan khusus member berlisensi. Masuk atau daftar sebagai owner toko terlebih dahulu.</p>
      <div className="flex gap-3">
        <Button asChild size="lg" data-testid="download-login-btn"><Link to="/login?next=/unduh">Masuk</Link></Button>
        <Button asChild size="lg" variant="outline" data-testid="download-register-btn"><Link to="/daftar">Daftar</Link></Button>
      </div>{meta}
    </div>
  );
  if (!isOwner) return <div className="text-sm rounded-lg bg-muted px-4 py-3" data-testid="download-owner-only">Hanya owner toko yang dapat membeli & mengunduh installer.</div>;
  if (lic === null) return <Loader2 className="h-5 w-5 animate-spin" />;
  return <div className="space-y-2 w-full">{lic && <LicensePanel data={lic} reload={load} />}{meta}</div>;
};

export default function DownloadDesktop() {
  const [rel, setRel] = useState(null);
  useEffect(() => { api.get("/desktop/release").then(({ data }) => setRel(data)).catch(() => setRel({ available: false })); }, []);
  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between px-6 lg:px-12 py-5 border-b">
        <Link to="/login"><img src={BRAND.logoFull} alt={BRAND.name} className="h-10 w-auto" /></Link>
        <ThemeToggle />
      </header>
      <main className="px-6 lg:px-12 py-12 lg:py-20 grid lg:grid-cols-[1.1fr_1fr] gap-14 max-w-6xl mx-auto">
        <section className="space-y-8 fade-up">
          <div className="inline-flex items-center gap-2 text-xs font-semibold rounded-full bg-primary/10 text-primary px-3 py-1"><MonitorSmartphone className="h-3.5 w-3.5" />Windows 10 / 11 · 64-bit</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">MUFA POS untuk Desktop</h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-xl">Software kasir yang terpasang di komputer Anda sendiri — tetap bisa dipakai sepenuhnya walau internet atau domain tidak aktif.</p>
          <div className="max-w-xl"><Access rel={rel} /></div>
          <ol className="space-y-3 pt-4">
            {STEPS.map((s, i) => (
              <li key={s} className="flex gap-3 text-sm"><span className="h-6 w-6 shrink-0 rounded-full bg-primary text-primary-foreground grid place-items-center text-xs font-bold">{i + 1}</span>{s}</li>
            ))}
          </ol>
        </section>
        <section className="grid sm:grid-cols-2 gap-4 content-start">
          {POINTS.map(([Icon, t, d], i) => (
            <div key={t} className="rounded-2xl border p-5 space-y-2 fade-up" style={{ animationDelay: `${i * 80}ms` }}>
              <Icon className="h-6 w-6 text-primary" />
              <div className="font-semibold">{t}</div>
              <p className="text-sm text-muted-foreground">{d}</p>
            </div>
          ))}
          <div className="sm:col-span-2 rounded-2xl bg-[#0B0F17] text-white p-5 text-sm flex gap-3">
            <CheckCircle2 className="h-5 w-5 text-amber-400 shrink-0" />
            Data offline tetap milik Anda. Lakukan backup rutin dari menu Backup Data untuk keamanan ekstra.
          </div>
        </section>
      </main>
    </div>
  );
}
